import * as React from "react";
import { useCallback, useMemo } from "react";
import { Button, styled } from "@mui/material";
import {
  commonBoxShadowStyle,
  commonBoxShadowStyleByOpacity,
} from "fe-common/ui/boxShadow";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../../util/aKaunGeneralUtil";

//region [[ Styles ]]

const LandingPageCardView = styled("div")(({ theme }) => ({
  ...commonBoxShadowStyle,
  cursor: "pointer",
  background: KahunColorMap.White,
  borderRadius: "14px",
  display: "flex",
  flexFlow: "column",
  padding: "15px",
  maxWidth: "311px",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "215px",
  "&:hover": {
    ...commonBoxShadowStyleByOpacity("0.75"),
  },
  "& > *:not(:last-child)": {
    marginBottom: "12px",
    [theme.breakpoints.down(MobileView)]: {
      marginBottom: "0",
    },
  },
  [theme.breakpoints.down(MobileView)]: {
    minHeight: "auto",
  },
}));

const Top = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  justifyContent: "space-around",
  alignItems: "center",
  flexGrow: 1,
}));

const Bottom = styled("div")(({ theme }) => ({
  textAlign: "center",
  fontSize: "13px",
  [theme.breakpoints.down(MobileView)]: {
    padding: "7px",
    paddingTop: "12px",
  },
}));

const CardTitle = styled("span")(({ theme }) => ({
  fontSize: "17px",
  lineHeight: "17px",
  fontWeight: 700,
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    lineHeight: "22px",
  },
}));

const CardFooter = styled("span")({
  fontSize: "12px",
  lineHeight: "17px",
  "& a, & a:hover, & a:visited, & a:active": {
    textDecoration: "underline",
    color: KahunColorMap.PrimaryDim,
  },
});

const FooterLink = styled(Button)({
  display: "inline",
  fontSize: "12px",
  lineHeight: "17px",
  textTransform: "none",
  textDecoration: "underline",
  color: KahunColorMap.PrimaryDim,
  background: "none",
  "&:hover": {
    background: "none",
  },
  margin: 0,
  padding: 0,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface LandingPageCardProps {
  className?: string;
  onClick: () => void;
  title?: string;
  actionView?: React.ReactNode;
  bottomText?: string;
  onBottomActionClicked?: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LandingPageCard = ({
  className,
  onClick,
  title,
  actionView,
  bottomText,
  onBottomActionClicked,
  ...props
}: LandingPageCardProps) => {
  const handleLinkClicked = useCallback(
    (e) => {
      e.stopPropagation();
      if (onBottomActionClicked) onBottomActionClicked();
    },
    [onBottomActionClicked],
  );

  const footer = useMemo(() => {
    const [preLinkText, linkText, postLinkText] = bottomText.split(
      /\[linkText=\](.*?)\[\/linkText]/g,
    );
    return (
      <CardFooter>
        {preLinkText}
        {linkText && linkText !== "" && (
          <FooterLink onClick={handleLinkClicked}>{linkText}</FooterLink>
        )}
        {postLinkText}
      </CardFooter>
    );
  }, [bottomText, handleLinkClicked]);

  return (
    <LandingPageCardView onClick={onClick} className={className}>
      <Top className={"upper-section"}>
        {title && <CardTitle>{title}</CardTitle>}
        {actionView}
      </Top>
      <Bottom className={"lower-section"}>{footer}</Bottom>
    </LandingPageCardView>
  );
};
